/**
 * Moteur comptable - couche d'appel RPC
 * La logique métier vit en SQL (migrations 056, 057).
 * Ce module expose les appels pour générer les écritures à partir des événements.
 * Ne pas appeler depuis React sauf au bon moment (après validation métier).
 */

import { supabase } from "../supabase";
import { logger } from "../logger";

/** Écriture d'attente à la création : trésorerie (compte saisi sur la transaction ou 521/571/552x) / 471 ou l’inverse. */
export async function genererEcritureTransactionAttenteBancaire(
  transactionId: string
): Promise<string | null> {
  const { data, error } = await supabase.rpc("generer_ecriture_transaction_attente_bancaire", {
    p_transaction_id: transactionId,
  });
  if (error) throw error;
  return data as string | null;
}

/** @deprecated Délégué à {@link genererEcritureTransactionAttenteBancaire} (entrée + sortie). */
export async function genererEcritureTransactionDepenseInjustifiee(
  transactionId: string
): Promise<string | null> {
  const { data, error } = await supabase.rpc("generer_ecriture_transaction_depense_injustifiee", {
    p_transaction_id: transactionId,
  });
  if (error) throw error;
  return data as string | null;
}

/** Génère l'écriture pour une facture achat validée (D6/D4452/C4011) et, si TVA sur encaissements, l'écriture de rétention D4454/C4452. */
export async function genererEcritureFactureAchatValidee(
  factureAchatId: string
): Promise<string | null> {
  const { data, error } = await supabase.rpc("generer_ecriture_facture_achat_validee", {
    p_facture_achat_id: factureAchatId,
  });
  if (error) {
    logger.error("[COMPTA] RPC generer_ecriture_facture_achat_validee erreur:", error.code, error.message, error.details);
    throw error;
  }
  return data as string | null;
}

/** Écriture quittance loyer : D622x HT, D4452 TVA si > 0, C4477 RSL, C401 net (métadonnées `metadata.quittance_loyer`). */
export async function genererEcritureQuittanceLoyerFactureAchat(factureAchatId: string): Promise<string | null> {
  const { data, error } = await supabase.rpc("generer_ecriture_quittance_loyer_facture_achat", {
    p_facture_achat_id: factureAchatId,
  });
  if (error) {
    logger.error(
      "[COMPTA] RPC generer_ecriture_quittance_loyer_facture_achat erreur:",
      error.code,
      error.message,
      error.details
    );
    throw error;
  }
  return data as string | null;
}

/** Écriture OD CCA (D476 / C charge) en date de clôture, extournable le lendemain. */
export async function genererEcritureFactureAchatCca(factureAchatId: string): Promise<string | null> {
  const { data, error } = await supabase.rpc("generer_ecriture_facture_achat_cca", {
    p_facture_achat_id: factureAchatId,
  });
  if (error) {
    logger.error("[COMPTA] RPC generer_ecriture_facture_achat_cca erreur:", error.code, error.message, error.details);
    throw error;
  }
  return data as string | null;
}

/** OD FNP au 31/12/N (exercice cible), extournable au 1er jour de l’exercice suivant. */
export async function genererEcritureFactureAchatFnp(
  factureAchatId: string,
  exerciceCibleId: string
): Promise<string | null> {
  const { data, error } = await supabase.rpc("generer_ecriture_facture_achat_fnp", {
    p_facture_achat_id: factureAchatId,
    p_exercice_cible_id: exerciceCibleId,
  });
  if (error) {
    logger.error("[COMPTA] RPC generer_ecriture_facture_achat_fnp erreur:", error.code, error.message, error.details);
    throw error;
  }
  return data as string | null;
}

/** Crée les écritures miroir pour les OD extournables dont date_extourne = p_date. */
export async function genererExtournesAu(dossierId: string, dateIso: string): Promise<number> {
  const { data, error } = await supabase.rpc("generer_extournes_au", {
    p_dossier_id: dossierId,
    p_date: dateIso.slice(0, 10),
  });
  if (error) {
    logger.error("[COMPTA] RPC generer_extournes_au erreur:", error.code, error.message, error.details);
    throw error;
  }
  return typeof data === "number" ? data : Number(data) || 0;
}

/** Génère l'écriture de paiement (D fournisseur auxiliaire ou 4011 / C521) et, si TVA sur encaissements, l'écriture D4452/C4454 au prorata. */
export async function genererEcritureTransactionDepenseRapprochee(
  factureAchatId: string,
  transactionId: string,
  montant: number
): Promise<string | null> {
  const { data, error } = await supabase.rpc("generer_ecriture_transaction_depense_rapprochee", {
    p_facture_achat_id: factureAchatId,
    p_transaction_id: transactionId,
    p_montant: montant,
  });
  if (error) {
    logger.error("[COMPTA] RPC generer_ecriture_transaction_depense_rapprochee erreur:", error.code, error.message, error.details);
    throw error;
  }
  return data as string | null;
}

/** Génère l'écriture pour une trésorerie directe validée (D6/C521) */
export async function genererEcritureTresorerieDirecteValidee(
  transactionId: string
): Promise<string | null> {
  const { data, error } = await supabase.rpc("generer_ecriture_tresorerie_directe_validee", {
    p_transaction_id: transactionId,
  });
  if (error) {
    logger.error("[COMPTA] RPC generer_ecriture_tresorerie_directe_validee erreur:", error.code, error.message, error.details);
    throw error;
  }
  return data as string | null;
}

/** Génère l'écriture pour une facture vente envoyée (D411/C443/C70) */
export async function genererEcritureFactureVenteEnvoyee(
  factureId: string
): Promise<string | null> {
  const { data, error } = await supabase.rpc("generer_ecriture_facture_vente_envoyee", {
    p_facture_id: factureId,
  });
  if (error) throw error;
  return data as string | null;
}

/** Écriture VTE pour une facture de vente importée validée (sans ligne dans `factures`). */
export async function genererEcritureFactureVenteImportee(importId: string): Promise<string | null> {
  const { data, error } = await supabase.rpc("generer_ecriture_facture_vente_importee", {
    p_import_id: importId,
  });
  if (error) throw error;
  return data as string | null;
}

/** Génère l'écriture pour un encaissement facture vente (D521/C411) */
export async function genererEcritureRecetteFactureVente(
  facturePaiementId: string
): Promise<string | null> {
  const { data, error } = await supabase.rpc("generer_ecriture_recette_facture_vente", {
    p_facture_paiement_id: facturePaiementId,
  });
  if (error) throw error;
  return data as string | null;
}

/** Génère l'écriture pour un avoir de vente (D70/D443/C411) — mapping provisoire */
export async function genererEcritureFactureVenteAvoirEnvoyee(
  factureId: string
): Promise<string | null> {
  const { data, error } = await supabase.rpc("generer_ecriture_facture_vente_avoir_envoyee", {
    p_facture_id: factureId,
  });
  if (error) throw error;
  return data as string | null;
}

/** Génère l'écriture remboursement client (D411/C521) — avoir + sortie uniquement */
export async function genererEcritureRemboursementClient(
  facturePaiementId: string
): Promise<string | null> {
  const { data, error } = await supabase.rpc("generer_ecriture_remboursement_client", {
    p_facture_paiement_id: facturePaiementId,
  });
  if (error) throw error;
  return data as string | null;
}

// ─── Lecture des écritures (pour vue Compta) ───────────────────────────────────

export interface EcritureLigne {
  id: string;
  compte_numero: string;
  compte_libelle: string;
  debit: number;
  credit: number;
  libelle_ligne: string | null;
}

export interface EcritureComptable {
  id: string;
  journal_code: string;
  date_ecriture: string;
  date_piece: string;
  libelle: string;
  source_type: string;
  source_id: string;
  event_type: string;
  statut: "draft" | "validated";
  /** Écriture provisoire 471 en attente d'affectation. */
  is_attente?: boolean;
  /** Écriture d'inventaire / CCA à extourner (ex. ouverture N+1). */
  extournable?: boolean;
  date_extourne?: string | null;
  lignes: EcritureLigne[];
}

/** Récupère l'écriture "facture achat validée" pour une facture d'achat. */
export async function fetchEcritureFactureAchat(
  factureAchatId: string
): Promise<EcritureComptable | null> {
  const { data: ecritures, error: eErr } = await supabase
    .from("ecritures_comptables")
    .select("id, journal_code, date_ecriture, date_piece, libelle, source_type, source_id, event_type, statut")
    .eq("source_type", "facture_achat")
    .eq("source_id", factureAchatId)
    .eq("event_type", "facture_achat_validee")
    .limit(1);
  if (eErr) {
    logger.warn("[COMPTA] fetchEcritureFactureAchat erreur:", eErr.code, eErr.message);
    return null;
  }
  if (!ecritures?.length) return null;
  const ec = ecritures[0] as EcritureComptable;
  const { data: lignes, error: lErr } = await supabase
    .from("ecritures_comptables_lignes")
    .select("id, compte_numero, compte_libelle, debit, credit, libelle_ligne")
    .eq("ecriture_id", ec.id)
    .order("created_at");
  if (lErr) return { ...ec, lignes: [] };
  ec.lignes = (lignes || []).map((l) => ({
    ...l,
    debit: Number(l.debit) || 0,
    credit: Number(l.credit) || 0,
  }));
  return ec;
}

/** Écriture liée à une quittance de loyer (facture achat `type_document = quittance_loyer`). */
export async function fetchEcritureQuittanceLoyerFactureAchat(
  factureAchatId: string
): Promise<EcritureComptable | null> {
  const { data: ecritures, error: eErr } = await supabase
    .from("ecritures_comptables")
    .select("id, journal_code, date_ecriture, date_piece, libelle, source_type, source_id, event_type, statut")
    .eq("source_type", "facture_achat")
    .eq("source_id", factureAchatId)
    .eq("event_type", "quittance_loyer_facture_achat")
    .limit(1);
  if (eErr) {
    logger.warn("[COMPTA] fetchEcritureQuittanceLoyerFactureAchat erreur:", eErr.code, eErr.message);
    return null;
  }
  if (!ecritures?.length) return null;
  const ec = ecritures[0] as EcritureComptable;
  const { data: lignes, error: lErr } = await supabase
    .from("ecritures_comptables_lignes")
    .select("id, compte_numero, compte_libelle, debit, credit, libelle_ligne")
    .eq("ecriture_id", ec.id)
    .order("created_at");
  if (lErr) return { ...ec, lignes: [] };
  ec.lignes = (lignes || []).map((l) => ({
    ...l,
    debit: Number(l.debit) || 0,
    credit: Number(l.credit) || 0,
  }));
  return ec;
}

/** Écriture CCA liée à une facture d'achat (si générée). */
export async function fetchEcritureFactureAchatCca(factureAchatId: string): Promise<EcritureComptable | null> {
  const { data: ecritures, error: eErr } = await supabase
    .from("ecritures_comptables")
    .select(
      "id, journal_code, date_ecriture, date_piece, libelle, source_type, source_id, event_type, statut, extournable, date_extourne"
    )
    .eq("source_type", "facture_achat")
    .eq("source_id", factureAchatId)
    .eq("event_type", "facture_achat_cca")
    .limit(1);
  if (eErr) {
    logger.warn("[COMPTA] fetchEcritureFactureAchatCca erreur:", eErr.code, eErr.message);
    return null;
  }
  if (!ecritures?.length) return null;
  const ec = ecritures[0] as EcritureComptable;
  const { data: lignes, error: lErr } = await supabase
    .from("ecritures_comptables_lignes")
    .select("id, compte_numero, compte_libelle, debit, credit, libelle_ligne")
    .eq("ecriture_id", ec.id)
    .order("created_at");
  if (lErr) return { ...ec, lignes: [] };
  ec.lignes = (lignes || []).map((l) => ({
    ...l,
    debit: Number(l.debit) || 0,
    credit: Number(l.credit) || 0,
  }));
  return ec;
}

/** Écriture de rétention TVA (TVA sur encaissements) : D4454 / C4452. */
export async function fetchEcritureRetentionTvaFactureAchat(
  factureAchatId: string
): Promise<EcritureComptable | null> {
  const { data: ecritures, error: eErr } = await supabase
    .from("ecritures_comptables")
    .select("id, journal_code, date_ecriture, date_piece, libelle, source_type, source_id, event_type, statut")
    .eq("source_type", "facture_achat")
    .eq("source_id", factureAchatId)
    .eq("event_type", "facture_achat_retention_tva")
    .limit(1);
  if (eErr) {
    logger.warn("[COMPTA] fetchEcritureRetentionTvaFactureAchat erreur:", eErr.code, eErr.message);
    return null;
  }
  if (!ecritures?.length) return null;
  const ec = ecritures[0] as EcritureComptable;
  const { data: lignes, error: lErr } = await supabase
    .from("ecritures_comptables_lignes")
    .select("id, compte_numero, compte_libelle, debit, credit, libelle_ligne")
    .eq("ecriture_id", ec.id)
    .order("created_at");
  if (lErr) return { ...ec, lignes: [] };
  ec.lignes = (lignes || []).map((l) => ({
    ...l,
    debit: Number(l.debit) || 0,
    credit: Number(l.credit) || 0,
  }));
  return ec;
}

/** Récupère les écritures de paiement (rapprochements) pour une facture d'achat. */
export async function fetchEcrituresPaiementFactureAchat(
  factureAchatId: string
): Promise<EcritureComptable[]> {
  const { data: rapprochements, error: rErr } = await supabase
    .from("facture_achat_rapprochements")
    .select("id, transaction_id, montant")
    .eq("facture_achat_id", factureAchatId);
  if (rErr) {
    logger.warn("[COMPTA] fetchEcrituresPaiementFactureAchat rapprochements erreur:", rErr.code, rErr.message);
    return [];
  }
  if (!rapprochements?.length) return [];
  const ids = rapprochements.map((r) => r.id);
  const { data: ecritures, error: eErr } = await supabase
    .from("ecritures_comptables")
    .select("id, journal_code, date_ecriture, date_piece, libelle, source_type, source_id, event_type, statut")
    .eq("source_type", "facture_achat_rapprochement")
    .in("source_id", ids)
    .order("date_ecriture", { ascending: true });
  if (eErr) {
    logger.warn("[COMPTA] fetchEcrituresPaiementFactureAchat ecritures erreur:", eErr.code, eErr.message);
    return [];
  }
  if (!ecritures?.length) {
    for (const r of rapprochements as { id: string; transaction_id: string; montant: number }[]) {
      try {
        await genererEcritureTransactionDepenseRapprochee(factureAchatId, r.transaction_id, Number(r.montant) || 0);
      } catch (err) {
        logger.error("[COMPTA] Génération différée paiement échouée:", r.id, err);
      }
    }
    const { data: refetched } = await supabase
      .from("ecritures_comptables")
      .select("id, journal_code, date_ecriture, date_piece, libelle, source_type, source_id, event_type, statut")
      .eq("source_type", "facture_achat_rapprochement")
      .in("source_id", ids)
      .order("date_ecriture", { ascending: true });
    if (!refetched?.length) return [];
    const refOut: EcritureComptable[] = [];
    for (const ec of refetched as EcritureComptable[]) {
      const { data: lignes, error: lErr } = await supabase
        .from("ecritures_comptables_lignes")
        .select("id, compte_numero, compte_libelle, debit, credit, libelle_ligne")
        .eq("ecriture_id", ec.id)
        .order("created_at");
      ec.lignes = lErr ? [] : (lignes || []).map((l) => ({
        ...l,
        debit: Number(l.debit) || 0,
        credit: Number(l.credit) || 0,
      }));
      refOut.push(ec);
    }
    return refOut;
  }
  const out: EcritureComptable[] = [];
  for (const ec of ecritures as EcritureComptable[]) {
    const { data: lignes, error: lErr } = await supabase
      .from("ecritures_comptables_lignes")
      .select("id, compte_numero, compte_libelle, debit, credit, libelle_ligne")
      .eq("ecriture_id", ec.id)
      .order("created_at");
    ec.lignes = lErr ? [] : (lignes || []).map((l) => ({
      ...l,
      debit: Number(l.debit) || 0,
      credit: Number(l.credit) || 0,
    }));
    out.push(ec);
  }
  return out;
}

/** Récupère l'écriture "facture vente envoyée" pour une facture de vente. */
export async function fetchEcritureFactureVente(
  factureId: string
): Promise<EcritureComptable | null> {
  const { data: ecritures, error: eErr } = await supabase
    .from("ecritures_comptables")
    .select("id, journal_code, date_ecriture, date_piece, libelle, source_type, source_id, event_type, statut")
    .eq("source_type", "facture_vente")
    .eq("source_id", factureId)
    .eq("event_type", "facture_vente_envoyee")
    .limit(1);
  if (eErr) {
    logger.warn("[COMPTA] fetchEcritureFactureVente erreur:", eErr.code, eErr.message);
    return null;
  }
  if (!ecritures?.length) return null;
  const ec = ecritures[0] as EcritureComptable;
  const { data: lignes, error: lErr } = await supabase
    .from("ecritures_comptables_lignes")
    .select("id, compte_numero, compte_libelle, debit, credit, libelle_ligne")
    .eq("ecriture_id", ec.id)
    .order("created_at");
  if (lErr) return { ...ec, lignes: [] };
  ec.lignes = (lignes || []).map((l) => ({
    ...l,
    debit: Number(l.debit) || 0,
    credit: Number(l.credit) || 0,
  }));
  return ec;
}

/** Récupère l'écriture d'une facture de vente importée. */
export async function fetchEcritureFactureVenteImportee(
  importId: string
): Promise<EcritureComptable | null> {
  const { data: ecritures, error: eErr } = await supabase
    .from("ecritures_comptables")
    .select("id, journal_code, date_ecriture, date_piece, libelle, source_type, source_id, event_type, statut")
    .eq("source_type", "facture_vente_import")
    .eq("source_id", importId)
    .eq("event_type", "facture_vente_import_validee")
    .limit(1);
  if (eErr) {
    logger.warn("[COMPTA] fetchEcritureFactureVenteImportee erreur:", eErr.code, eErr.message);
    return null;
  }
  if (!ecritures?.length) return null;
  const ec = ecritures[0] as EcritureComptable;
  const { data: lignes, error: lErr } = await supabase
    .from("ecritures_comptables_lignes")
    .select("id, compte_numero, compte_libelle, debit, credit, libelle_ligne")
    .eq("ecriture_id", ec.id)
    .order("created_at");
  if (lErr) return { ...ec, lignes: [] };
  ec.lignes = (lignes || []).map((l) => ({
    ...l,
    debit: Number(l.debit) || 0,
    credit: Number(l.credit) || 0,
  }));
  return ec;
}

/** Récupère l'écriture "avoir vente envoyé" pour un avoir. */
export async function fetchEcritureAvoirVente(
  factureId: string
): Promise<EcritureComptable | null> {
  const { data: ecritures, error: eErr } = await supabase
    .from("ecritures_comptables")
    .select("id, journal_code, date_ecriture, date_piece, libelle, source_type, source_id, event_type, statut")
    .eq("source_type", "facture_vente")
    .eq("source_id", factureId)
    .eq("event_type", "facture_vente_avoir_envoyee")
    .limit(1);
  if (eErr) {
    logger.warn("[COMPTA] fetchEcritureAvoirVente erreur:", eErr.code, eErr.message);
    return null;
  }
  if (!ecritures?.length) return null;
  const ec = ecritures[0] as EcritureComptable;
  const { data: lignes, error: lErr } = await supabase
    .from("ecritures_comptables_lignes")
    .select("id, compte_numero, compte_libelle, debit, credit, libelle_ligne")
    .eq("ecriture_id", ec.id)
    .order("created_at");
  if (lErr) return { ...ec, lignes: [] };
  ec.lignes = (lignes || []).map((l) => ({
    ...l,
    debit: Number(l.debit) || 0,
    credit: Number(l.credit) || 0,
  }));
  return ec;
}

/** Récupère les écritures d'encaissement (facture) ou remboursement (avoir) pour une facture/avoir vente.
 * Facture : event_type encaissee, génération différée si manquant.
 * Avoir : event_type rembourse (sortie uniquement) ; compensation entrée = pas d'écriture. */
export async function fetchEcrituresEncaissementFactureVente(
  factureId: string,
  opts?: { isFacture?: boolean }
): Promise<EcritureComptable[]> {
  const { data: paiements, error: pErr } = await supabase
    .from("facture_paiements")
    .select("id, transaction_id")
    .eq("facture_id", factureId);
  if (pErr) {
    logger.warn("[COMPTA] fetchEcrituresEncaissementFactureVente paiements erreur:", pErr.code, pErr.message);
    return [];
  }
  if (!paiements?.length) return [];
  const ids = paiements.map((p) => p.id);
  const eventType = opts?.isFacture !== false ? "facture_vente_paiement_encaissee" : "facture_vente_paiement_rembourse";

  const { data: ecritures, error: eErr } = await supabase
    .from("ecritures_comptables")
    .select("id, journal_code, date_ecriture, date_piece, libelle, source_type, source_id, event_type, statut")
    .eq("source_type", "facture_vente_paiement")
    .eq("event_type", eventType)
    .in("source_id", ids)
    .order("date_ecriture", { ascending: true });
  if (eErr) {
    logger.warn("[COMPTA] fetchEcrituresEncaissementFactureVente ecritures erreur:", eErr.code, eErr.message);
    return [];
  }
  if (!ecritures?.length && opts?.isFacture === false) {
    const txIds = (paiements as { id: string; transaction_id: string | null }[])
      .map((p) => p.transaction_id)
      .filter((id): id is string => !!id);
    if (txIds.length > 0) {
      const { data: txs } = await supabase.from("transactions").select("id, type").in("id", txIds);
      const sortieTxIds = new Set((txs || []).filter((t) => (t as { type?: string }).type === "sortie").map((t) => t.id));
      for (const p of paiements as { id: string; transaction_id: string | null }[]) {
        if (p.transaction_id && sortieTxIds.has(p.transaction_id)) {
          try {
            await genererEcritureRemboursementClient(p.id);
          } catch (err) {
            logger.error("[COMPTA] Génération différée remboursement échouée:", p.id, err);
          }
        }
      }
    }
    const { data: refetched, error: refErr } = await supabase
      .from("ecritures_comptables")
      .select("id, journal_code, date_ecriture, date_piece, libelle, source_type, source_id, event_type, statut")
      .eq("source_type", "facture_vente_paiement")
      .eq("event_type", eventType)
      .in("source_id", ids)
      .order("date_ecriture", { ascending: true });
    if (refErr || !refetched?.length) return [];
    const refOut: EcritureComptable[] = [];
    for (const ec of refetched as EcritureComptable[]) {
      const { data: lignes, error: lErr } = await supabase
        .from("ecritures_comptables_lignes")
        .select("id, compte_numero, compte_libelle, debit, credit, libelle_ligne")
        .eq("ecriture_id", ec.id)
        .order("created_at");
      ec.lignes = lErr ? [] : (lignes || []).map((l) => ({
        ...l,
        debit: Number(l.debit) || 0,
        credit: Number(l.credit) || 0,
      }));
      refOut.push(ec);
    }
    return refOut;
  }
  if (!ecritures?.length && opts?.isFacture !== false) {
    for (const p of paiements as { id: string }[]) {
      try {
        await genererEcritureRecetteFactureVente(p.id);
      } catch (err) {
        logger.error("[COMPTA] Génération différée encaissement échouée:", p.id, err);
      }
    }
    const { data: refetched, error: refErr } = await supabase
      .from("ecritures_comptables")
      .select("id, journal_code, date_ecriture, date_piece, libelle, source_type, source_id, event_type, statut")
      .eq("source_type", "facture_vente_paiement")
      .eq("event_type", eventType)
      .in("source_id", ids)
      .order("date_ecriture", { ascending: true });
    if (refErr || !refetched?.length) return [];
    const refOut: EcritureComptable[] = [];
    for (const ec of refetched as EcritureComptable[]) {
      const { data: lignes, error: lErr } = await supabase
        .from("ecritures_comptables_lignes")
        .select("id, compte_numero, compte_libelle, debit, credit, libelle_ligne")
        .eq("ecriture_id", ec.id)
        .order("created_at");
      ec.lignes = lErr ? [] : (lignes || []).map((l) => ({
        ...l,
        debit: Number(l.debit) || 0,
        credit: Number(l.credit) || 0,
      }));
      refOut.push(ec);
    }
    return refOut;
  }
  if (!ecritures?.length) return [];
  const out: EcritureComptable[] = [];
  for (const ec of ecritures as EcritureComptable[]) {
    const { data: lignes, error: lErr } = await supabase
      .from("ecritures_comptables_lignes")
      .select("id, compte_numero, compte_libelle, debit, credit, libelle_ligne")
      .eq("ecriture_id", ec.id)
      .order("created_at");
    ec.lignes = lErr ? [] : (lignes || []).map((l) => ({
      ...l,
      debit: Number(l.debit) || 0,
      credit: Number(l.credit) || 0,
    }));
    out.push(ec);
  }
  return out;
}

// ─── Écritures par transaction (vue Compta) ───────────────────────────────────

export type TransactionComptableContext =
  | "direct"
  | "achat"
  | "vente_encaissement"
  | "vente_remboursement";

/** Récupère les écritures comptables liées à une transaction selon son contexte. */
export async function fetchEcrituresForTransaction(
  transactionId: string,
  context: TransactionComptableContext
): Promise<{ ecritures: EcritureComptable[] }> {
  if (context === "direct") {
    const { data: ecritures, error } = await supabase
      .from("ecritures_comptables")
      .select("id, journal_code, date_ecriture, date_piece, libelle, source_type, source_id, event_type, statut")
      .eq("source_type", "transaction")
      .eq("source_id", transactionId)
      .order("date_ecriture", { ascending: true });
    if (error) return { ecritures: [] };
    const out: EcritureComptable[] = [];
    for (const ec of ecritures || []) {
      const { data: lignes } = await supabase
        .from("ecritures_comptables_lignes")
        .select("id, compte_numero, compte_libelle, debit, credit, libelle_ligne")
        .eq("ecriture_id", ec.id)
        .order("created_at");
      (ec as EcritureComptable).lignes = (lignes || []).map((l) => ({
        ...l,
        debit: Number(l.debit) || 0,
        credit: Number(l.credit) || 0,
      }));
      out.push(ec as EcritureComptable);
    }
    return { ecritures: out };
  }

  if (context === "achat") {
    const { data: farList } = await supabase
      .from("facture_achat_rapprochements")
      .select("id, facture_achat_id, transaction_id")
      .eq("transaction_id", transactionId);
    const factureAchatIds = [...new Set((farList || []).map((r) => (r as { facture_achat_id: string }).facture_achat_id))];
    const ecritures: EcritureComptable[] = [];
    for (const faId of factureAchatIds) {
      const ecrituresPaiement = await fetchEcrituresPaiementFactureAchat(faId);
      const rappIds = (farList || []).filter((r) => (r as { facture_achat_id: string }).facture_achat_id === faId).map((r) => (r as { id: string }).id);
      const paiements = ecrituresPaiement.filter((e) => rappIds.includes(e.source_id));
      ecritures.push(...paiements);
    }
    const seen = new Set<string>();
    const unique = ecritures.filter((e) => { if (seen.has(e.id)) return false; seen.add(e.id); return true; });
    return { ecritures: unique };
  }

  if (context === "vente_encaissement" || context === "vente_remboursement") {
    const { data: paiements } = await supabase
      .from("facture_paiements")
      .select("facture_id")
      .eq("transaction_id", transactionId);
    const factureIds = [...new Set((paiements || []).map((p) => (p as { facture_id: string }).facture_id))];
    const ecritures: EcritureComptable[] = [];
    for (const fId of factureIds) {
      const { data: f } = await supabase.from("factures").select("facture_origine_id").eq("id", fId).single();
      const isAvoir = !!(f as { facture_origine_id?: string | null } | null)?.facture_origine_id;
      if (context === "vente_remboursement") {
        const [ecritureDoc, ecrituresPaiement] = await Promise.all([
          isAvoir ? fetchEcritureAvoirVente(fId) : fetchEcritureFactureVente(fId),
          fetchEcrituresEncaissementFactureVente(fId, { isFacture: !isAvoir }),
        ]);
        if (ecritureDoc) ecritures.push(ecritureDoc);
        ecritures.push(...ecrituresPaiement);
      } else {
        const ecrituresPaiement = await fetchEcrituresEncaissementFactureVente(fId, { isFacture: !isAvoir });
        ecritures.push(...ecrituresPaiement);
      }
    }
    const seen = new Set<string>();
    const unique = ecritures.filter((e) => { if (seen.has(e.id)) return false; seen.add(e.id); return true; });
    return { ecritures: unique };
  }

  return { ecritures: [] };
}

export * from "./journaux";
export * from "./balance";
